import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import i18n from '@/i18n';
import { useSignedImageSrc } from '@/hooks/useSignedImageSrc';
import { pickImageSignOptions, refreshSignedDisplayUrl } from '@/utils/imageDisplayAccess';
import { stripImageFileExtension } from '@/utils/imageUrlUtils';
import { getImageSpaceSourceMeta } from '@/pages/ipe/workbench/constants/imageSpaceSourceOptions';
import {
  beginNestedMediaPreview,
  endNestedMediaPreview,
  suppressHistoryBackdropClose,
} from '@/pages/ipe/workbench/utils/nestedMediaPreviewGuard';

function metaRow(label, value, key = label) {
  return (
    <div key={key} className="funding-image-space-preview__meta-row">
      <span className="funding-image-space-preview__meta-label">{label}</span>
      <span className="funding-image-space-preview__meta-value">{value ?? '—'}</span>
    </div>
  );
}

function inferImageFormatLabel(preview) {
  const stored = String(preview?.format || '').trim().replace(/^\./, '');
  if (stored && stored !== '—') return stored.replace(/^jpeg$/i, 'jpg');

  const fromName = (preview?.name || '').match(/\.([a-zA-Z0-9]+)$/);
  if (fromName) return fromName[1].toLowerCase();

  const mimeFromDataUrl = (s) => {
    if (typeof s !== 'string' || !s.startsWith('data:image/')) return '';
    const mime = s.slice('data:image/'.length).split(';')[0].split('+')[0].toLowerCase();
    if (mime === 'jpeg') return 'jpg';
    return mime || '';
  };

  const url = preview?.url || '';
  const fromPreviewMime = mimeFromDataUrl(url);
  if (fromPreviewMime) return fromPreviewMime;

  if (typeof url === 'string' && url.trim()) {
    const pathOnly = url.split('?')[0].replace(/\\/g, '/');
    const fromPath = pathOnly.match(/\.([a-zA-Z0-9]+)$/);
    if (fromPath) return fromPath[1].toLowerCase();
  }

  const fromStoredData = mimeFromDataUrl(preview?.dataUrl);
  if (fromStoredData) return fromStoredData;

  return '—';
}

function resolvePreviewSourceLabel(preview) {
  if (preview?.sourceLabel) {
    return preview.sourceGroupLabel
      ? `${preview.sourceGroupLabel} · ${preview.sourceLabel}`
      : preview.sourceLabel;
  }
  if (preview?.source || preview?.sourceType) {
    const meta = getImageSpaceSourceMeta(preview);
    return meta.groupLabel ? `${meta.groupLabel} · ${meta.label}` : meta.label;
  }
  return '';
}

function resolvePreviewSections(preview, metaSections) {
  if (Array.isArray(metaSections) && metaSections.length > 0) {
    return metaSections;
  }
  const rows = [];
  const nameLabel = stripImageFileExtension(preview?.name) || String(preview?.name || '').trim();
  if (nameLabel) rows.push({ label: i18n.t('workbench:imagePreview.name'), value: nameLabel });
  const formatLabel = inferImageFormatLabel(preview);
  if (formatLabel && formatLabel !== '—') {
    rows.push({ label: i18n.t('workbench:imagePreview.format'), value: formatLabel });
  }
  if (preview?.fileSizeLabel) {
    rows.push({ label: i18n.t('workbench:imagePreview.fileSize'), value: preview.fileSizeLabel });
  }
  if (preview?.width && preview?.height) {
    rows.push({
      label: i18n.t('workbench:imagePreview.dimensions'),
      value: `${preview.width} × ${preview.height} px`,
    });
  }
  const sourceLabel = resolvePreviewSourceLabel(preview);
  if (sourceLabel) rows.push({ label: i18n.t('workbench:imagePreview.source'), value: sourceLabel });
  if (preview?.createdAt) {
    rows.push({ label: i18n.t('workbench:imagePreview.createdAt'), value: preview.createdAt });
  }
  if (preview?.creatorLabel) {
    rows.push({ label: i18n.t('workbench:imagePreview.creator'), value: preview.creatorLabel });
  }
  if (rows.length === 0) return [];
  return [{ title: i18n.t('workbench:imagePreview.basicInfo'), rows }];
}

export function IpeImagePreviewOverlay({
  preview,
  scale,
  setScale,
  containerRef,
  onClose,
  navList = [],
  navIndex = -1,
  onNavigate,
  metaSections = null,
  onDownload,
  footerLeft = null,
  loading = false,
}) {
  const signOptions = pickImageSignOptions(preview);
  const signedSrc = useSignedImageSrc(preview?.url, signOptions);
  const [refreshedSrc, setRefreshedSrc] = useState('');
  const refreshInFlightRef = useRef(false);
  const previewUrl = refreshedSrc || signedSrc || '';
  const previewIdentity = `${preview?.id ?? ''}\0${preview?.url ?? ''}\0${preview?.displayUrl ?? ''}\0${preview?.viewUrl ?? ''}`;
  const loadedToken = `${previewIdentity}\0${previewUrl}`;
  const [loadedIdentity, setLoadedIdentity] = useState('');
  const imageReady = Boolean(previewUrl) && loadedIdentity === loadedToken;
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [imageSize, setImageSize] = useState({ width: null, height: null });
  const dragStateRef = useRef(null);

  useEffect(() => {
    setRefreshedSrc('');
    refreshInFlightRef.current = false;
  }, [preview?.url, preview?.id, preview?.displayUrl, preview?.viewUrl]);

  useEffect(() => {
    setOffset({ x: 0, y: 0 });
    setIsDragging(false);
    dragStateRef.current = null;
    setImageSize({
      width: preview?.width ?? null,
      height: preview?.height ?? null,
    });
  }, [preview?.url, preview?.id, preview?.displayUrl]);

  useEffect(() => {
    const el = containerRef?.current;
    if (!preview || !el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale((prev) => Math.max(0.1, Math.min(5, prev + delta)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [preview, setScale, containerRef]);

  useEffect(() => {
    if (!preview) return undefined;
    beginNestedMediaPreview();
    return () => {
      endNestedMediaPreview();
    };
  }, [preview]);

  useEffect(() => {
    if (!preview) return undefined;
    // 捕获阶段优先处理，避免 Esc 再冒泡到历史侧栏一并关闭
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        suppressHistoryBackdropClose();
        onClose?.();
      } else if (event.key === 'ArrowLeft' && onNavigate) {
        event.preventDefault();
        event.stopPropagation();
        onNavigate(-1);
      } else if (event.key === 'ArrowRight' && onNavigate) {
        event.preventDefault();
        event.stopPropagation();
        onNavigate(1);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [preview, onClose, onNavigate]);

  const closeFromMask = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
    // 先禁用历史遮罩，再延后卸载，彻底挡住 click 穿透
    suppressHistoryBackdropClose(500);
    window.setTimeout(() => onClose?.(), 50);
  }, [onClose]);

  const handlePointerDown = useCallback((event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [offset.x, offset.y]);

  const handlePointerMove = useCallback((event) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    setOffset({
      x: drag.originX + (event.clientX - drag.startX),
      y: drag.originY + (event.clientY - drag.startY),
    });
  }, []);

  const handlePointerUp = useCallback((event) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragStateRef.current = null;
    setIsDragging(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const handlePreviewImageError = useCallback(() => {
    const raw = String(preview?.url || '').trim();
    if (refreshInFlightRef.current || !raw) return;
    refreshInFlightRef.current = true;
    void (async () => {
      try {
        const next = await refreshSignedDisplayUrl(raw, signOptions);
        if (next) setRefreshedSrc(next);
      } catch (error) {
        console.warn('预览图签名地址刷新失败:', error);
      } finally {
        refreshInFlightRef.current = false;
      }
    })();
  }, [preview?.url, signOptions]);

  if (!preview) return null;

  const previewDisplayName = stripImageFileExtension(preview.name)
    || i18n.t('workbench:imagePreview.unnamed');
  const canPrev = navIndex > 0;
  const canNext = navIndex >= 0 && navIndex < navList.length - 1;
  const showNav = navList.length > 1 && typeof onNavigate === 'function';
  const sections = resolvePreviewSections(
    { ...preview, width: imageSize.width ?? preview.width, height: imageSize.height ?? preview.height },
    metaSections,
  );
  const mergedPreview = {
    ...preview,
    width: imageSize.width ?? preview.width,
    height: imageSize.height ?? preview.height,
  };

  return createPortal(
    <div
      className="funding-image-space-preview-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={previewDisplayName || i18n.t('workbench:imagePreview.title')}
      data-ipe-nested-media-preview="1"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) {
          event.preventDefault();
          event.stopPropagation();
          suppressHistoryBackdropClose(500);
        }
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          event.preventDefault();
          event.stopPropagation();
          suppressHistoryBackdropClose(500);
        }
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          closeFromMask(event);
        }
      }}
    >
      <div
        className="funding-image-space-preview-stack"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="funding-image-space-preview-shell">
          {showNav ? (
            <button
              type="button"
              className="funding-image-space-preview-nav"
              aria-label={i18n.t('workbench:imagePreview.prev')}
              disabled={!canPrev || loading}
              onClick={() => onNavigate(-1)}
            >
              <ChevronLeft size={22} strokeWidth={2.25} aria-hidden />
            </button>
          ) : (
            <span className="funding-image-space-preview-nav-spacer" aria-hidden />
          )}

          <div className="funding-image-space-preview-dialog">
            <button
              type="button"
              className="funding-image-space-preview-close"
              aria-label={i18n.t('workbench:imagePreview.close')}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onClose?.();
              }}
            >
              <X size={20} strokeWidth={2.25} aria-hidden />
            </button>

            <div
              ref={containerRef}
              className="funding-image-space-preview__viewport"
              style={{ cursor: isDragging ? 'grabbing' : 'default' }}
            >
              {loading && !previewUrl ? (
                <div className="funding-image-space-preview__placeholder">{i18n.t('workbench:imagePreview.loading')}</div>
              ) : previewUrl ? (
                <div className="funding-image-space-preview__stage">
                  {!imageReady ? (
                    <div className="funding-image-space-preview__placeholder">{i18n.t('workbench:imagePreview.loading')}</div>
                  ) : null}
                  <img
                    key={loadedToken}
                    src={previewUrl}
                    alt={previewDisplayName || i18n.t('workbench:imagePreview.title')}
                    draggable={false}
                    className="funding-image-space-preview__image"
                    style={{
                      cursor: isDragging ? 'grabbing' : 'grab',
                      transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                      transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                      visibility: imageReady ? 'visible' : 'hidden',
                      position: imageReady ? 'relative' : 'absolute',
                      pointerEvents: imageReady ? 'auto' : 'none',
                    }}
                    onLoad={(event) => {
                      const el = event.currentTarget;
                      const nw = el.naturalWidth;
                      const nh = el.naturalHeight;
                      setLoadedIdentity(loadedToken);
                      if (!nw || !nh) return;
                      setImageSize((prev) => ({
                        width: prev.width || nw,
                        height: prev.height || nh,
                      }));
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                    onError={handlePreviewImageError}
                    referrerPolicy="no-referrer"
                  />
                </div>
              ) : (
                <div className="funding-image-space-preview__placeholder">{i18n.t('workbench:imagePreview.loadingOrMissing')}</div>
              )}
            </div>

            <aside className="funding-image-space-preview__aside">
              <div className="funding-image-space-preview__title">
                {previewDisplayName}
              </div>
              {sections.map((section, sectionIndex) => (
                <React.Fragment key={section.title || sectionIndex}>
                  {section.title ? (
                    <div
                      className={`funding-image-space-preview__section-title${
                        sectionIndex > 0 ? ' funding-image-space-preview__section-title--spaced' : ''
                      }`}
                    >
                      {section.title}
                    </div>
                  ) : null}
                  {(section.rows || []).map((row) => {
                    if (row.node) {
                      return (
                        <div
                          key={row.label}
                          className={`funding-image-space-preview__meta-row${
                            row.variant ? ` funding-image-space-preview__meta-row--${row.variant}` : ''
                          }`}
                        >
                          <span className="funding-image-space-preview__meta-label">{row.label}</span>
                          <span className="funding-image-space-preview__meta-value">{row.node}</span>
                        </div>
                      );
                    }
                    return metaRow(row.label, row.value, `${sectionIndex}-${row.label}`);
                  })}
                </React.Fragment>
              ))}
              {!sections.length && mergedPreview.width && mergedPreview.height ? (
                <>
                  <div className="funding-image-space-preview__section-title">{i18n.t('workbench:imagePreview.basicInfo')}</div>
                  {metaRow(
                    i18n.t('workbench:imagePreview.dimensions'),
                    `${mergedPreview.width} × ${mergedPreview.height} px`,
                  )}
                </>
              ) : null}
            </aside>
          </div>

          {showNav ? (
            <button
              type="button"
              className="funding-image-space-preview-nav"
              aria-label={i18n.t('workbench:imagePreview.next')}
              disabled={!canNext || loading}
              onClick={() => onNavigate(1)}
            >
              <ChevronRight size={22} strokeWidth={2.25} aria-hidden />
            </button>
          ) : (
            <span className="funding-image-space-preview-nav-spacer" aria-hidden />
          )}
        </div>

        {(onDownload || footerLeft) ? (
          <div className="funding-image-space-preview-footer">
            {footerLeft || <span className="funding-image-space-preview-footer__spacer" aria-hidden />}
            {onDownload ? (
              <button
                type="button"
                className="funding-image-space-preview-action funding-image-space-preview-action--download"
                onClick={onDownload}
                disabled={!previewUrl}
              >
                <Download size={16} strokeWidth={2} aria-hidden />
                {i18n.t('workbench:imagePreview.download')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export function useIpeImagePreview() {
  const [previewImage, setPreviewImage] = useState(null);
  const [previewScale, setPreviewScale] = useState(1);
  const previewContainerRef = useRef(null);

  const openPreview = useCallback((imageUrl, imageName, options = {}) => {
    const raw = imageUrl && String(imageUrl).trim();
    if (!raw) return;
    const displayUrl = options.displayUrl || options.viewUrl || '';
    setPreviewImage({
      id: options.id != null ? String(options.id) : `${raw}-${Date.now()}`,
      url: raw,
      displayUrl,
      viewUrl: options.viewUrl || displayUrl,
      name: imageName || i18n.t('workbench:imagePreview.title'),
      ...options,
    });
    setPreviewScale(1);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewImage(null);
    setPreviewScale(1);
  }, []);

  return {
    previewImage,
    setPreviewImage,
    previewScale,
    setPreviewScale,
    previewContainerRef,
    openPreview,
    closePreview,
  };
}
